import { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { getAccount, documents as docsApi } from "../lib/supabase.js";
import QRGenerator from "../components/QRGenerator.jsx";
import { useUserRole, hasPermission } from "../App.jsx";

const platformUrl = import.meta.env.VITE_SUPABASE_URL;

// ─── Font loader ─────────────────────────────────────────────────────────────

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

// ─── Theme tokens ────────────────────────────────────────────────────────────

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", surfaceHover: "#f6f5f2", inputBg: "#f6f5f2",
};

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const inputStyle = {
  width: "100%", padding: "10px 14px", background: L.inputBg,
  border: `1px solid ${L.border}`, borderRadius: 8, color: L.text,
  fontSize: 13, outline: "none", fontFamily: sans, boxSizing: "border-box",
};

const labelStyle = {
  display: "block", fontSize: 10, color: L.muted, textTransform: "uppercase",
  letterSpacing: "0.1em", marginBottom: 6, fontWeight: 600,
};

const btnGhost = {
  padding: "8px 16px", background: "none", border: `1px solid ${L.border}`,
  borderRadius: 8, color: L.muted, fontSize: 12, fontWeight: 500,
  cursor: "pointer", fontFamily: sans, whiteSpace: "nowrap",
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function DocumentWidget() {
  useFonts();
  const account = getAccount();
  const color = account?.widget_color || "#6366f1";
  const { role } = useUserRole();
  if (!hasPermission(role, "editContent")) return <Navigate to="/" />;

  const [mode, setMode] = useState("float");
  const [position, setPosition] = useState("bottom-right");
  const [title, setTitle] = useState("Document Search");
  const [greeting, setGreeting] = useState("Search documents or ask a question");
  const [widgetColor, setWidgetColor] = useState(color);
  const [docCount, setDocCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const previewRef = useRef(null);

  useEffect(() => {
    docsApi.list().then(({ data }) => {
      setDocCount((data || []).filter((d) => d.status === "indexed").length);
    });
  }, []);

  const embedCode = mode === "float"
    ? `<script src="${platformUrl}/functions/v1/widget-documents-js?account=${account?.slug}"
  data-account="${account?.slug || "your-slug"}"
  data-api="${platformUrl}/functions/v1"
  data-mode="float"
  data-position="${position}"
  data-color="${widgetColor}"
  data-title="${title}"
  data-greeting="${greeting}"
  async></script>`
    : `<div id="clarifico-docs"></div>
<script src="${platformUrl}/functions/v1/widget-documents-js?account=${account?.slug}"
  data-account="${account?.slug || "your-slug"}"
  data-api="${platformUrl}/functions/v1"
  data-mode="inline"
  data-container="clarifico-docs"
  data-color="${widgetColor}"
  data-title="${title}"
  data-greeting="${greeting}"
  async></script>`;

  const copyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout>
      <style>{`
        @keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div style={{
        minHeight: "calc(100vh - 56px)", background: L.bg,
        margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text,
      }}>
        {/* ── Header ── */}
        <div style={{ marginBottom: 32, animation: "ov-up 0.5s ease both" }}>
          <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>
            Document Widget
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: L.muted }}>
            Deploy a document search and Q&A widget on any website · {docCount} document{docCount !== 1 ? "s" : ""} available
          </p>
        </div>

        {/* ── Config + Preview grid ── */}
        <div style={{
          display: "grid", gridTemplateColumns: "320px 1fr", gap: 24,
          animation: "ov-up 0.5s ease 0.08s both",
        }}>
          {/* Config panel */}
          <div>
            {/* Mode selector */}
            <div style={{
              background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
              padding: 22, marginBottom: 16,
            }}>
              <div style={{ ...labelStyle, marginBottom: 14, fontSize: 11 }}>Widget mode</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { id: "float", label: "Floating Bubble", desc: "Bottom corner chat bubble" },
                  { id: "inline", label: "Inline Panel", desc: "Embedded in a page section" },
                ].map((m) => {
                  const active = mode === m.id;
                  return (
                    <button
                      key={m.id} onClick={() => setMode(m.id)}
                      style={{
                        flex: 1, padding: "14px 10px", borderRadius: 8, textAlign: "center",
                        cursor: "pointer", fontFamily: sans, transition: "all 0.15s",
                        background: active ? L.surfaceHover : L.surface,
                        color: active ? L.text : L.muted,
                        border: `1px solid ${active ? L.text : L.border}`,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{m.label}</div>
                      <div style={{ fontSize: 10, marginTop: 3, color: L.muted }}>{m.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Appearance */}
            <div style={{
              background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
              padding: 22, marginBottom: 16,
            }}>
              <div style={{ ...labelStyle, marginBottom: 16, fontSize: 11 }}>Appearance</div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Widget title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Greeting message</label>
                <input value={greeting} onChange={(e) => setGreeting(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Accent color</label>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <input
                    type="color" value={widgetColor}
                    onChange={(e) => setWidgetColor(e.target.value)}
                    style={{ width: 36, height: 36, border: `1px solid ${L.border}`, borderRadius: 6, background: "none", cursor: "pointer", padding: 2 }}
                  />
                  <span style={{ fontFamily: "monospace", fontSize: 12, color: L.muted }}>{widgetColor}</span>
                </div>
              </div>
              {mode === "float" && (
                <div>
                  <label style={labelStyle}>Position</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["bottom-right", "bottom-left"].map((p) => {
                      const active = position === p;
                      return (
                        <button
                          key={p} onClick={() => setPosition(p)}
                          style={{
                            flex: 1, padding: "8px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                            cursor: "pointer", fontFamily: sans, transition: "all 0.15s",
                            background: active ? L.surfaceHover : L.surface,
                            color: active ? L.text : L.muted,
                            border: `1px solid ${active ? L.text : L.border}`,
                          }}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Embed code */}
            <div style={{
              background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 22,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ ...labelStyle, margin: 0, fontSize: 11 }}>Embed code</div>
                <button onClick={copyEmbed} style={{
                  ...btnGhost, padding: "5px 12px", fontSize: 11,
                  color: copied ? "#15803d" : L.text,
                  borderColor: copied ? "#bbf7d0" : L.border,
                  background: copied ? "#f0fdf4" : "none",
                }}>
                  {copied ? "✓ Copied" : "Copy code"}
                </button>
              </div>
              <pre style={{
                margin: 0, padding: 16, background: L.text, borderRadius: 8,
                color: "#a5b4fc", fontSize: 11, fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
                overflowX: "auto", lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-all",
              }}>
                {embedCode}
              </pre>
            </div>
          </div>

          {/* Live preview */}
          <div style={{
            background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 22,
          }}>
            <div style={{ ...labelStyle, marginBottom: 14, fontSize: 11 }}>Live preview</div>
            <div ref={previewRef} style={{
              background: "#f8f8f6", borderRadius: 10, position: "relative",
              minHeight: 520, overflow: "hidden", border: `1px solid ${L.borderLight}`,
            }}>
              {/* Skeleton page content */}
              <div style={{ padding: 28 }}>
                <div style={{ height: 14, width: 200, background: "#e4e3de", borderRadius: 4, marginBottom: 16 }} />
                <div style={{ height: 8, width: "92%", background: "#eae9e4", borderRadius: 3, marginBottom: 8 }} />
                <div style={{ height: 8, width: "78%", background: "#eae9e4", borderRadius: 3, marginBottom: 8 }} />
                <div style={{ height: 120, background: "#e4e3de", borderRadius: 8, marginBottom: 18 }} />
                <div style={{ height: 8, width: "88%", background: "#eae9e4", borderRadius: 3, marginBottom: 8 }} />
                <div style={{ height: 8, width: "62%", background: "#eae9e4", borderRadius: 3 }} />
              </div>

              {/* Widget preview */}
              {mode === "float" ? (
                <div style={{
                  position: "absolute", bottom: 18,
                  [position === "bottom-left" ? "left" : "right"]: 18,
                }}>
                  {/* Widget window */}
                  <div style={{
                    width: 300, background: "#fff", borderRadius: 14,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)", overflow: "hidden", marginBottom: 12,
                  }}>
                    <div style={{ padding: "13px 18px", background: widgetColor, color: "#fff" }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
                    </div>
                    <div style={{ display: "flex", borderBottom: "1px solid #f0eee9" }}>
                      {["Search", "Browse", "Ask AI"].map((t, i) => (
                        <div key={t} style={{
                          flex: 1, padding: "9px 4px", textAlign: "center", fontSize: 10, fontWeight: 600,
                          color: i === 0 ? widgetColor : "#aaa",
                          borderBottom: i === 0 ? `2px solid ${widgetColor}` : "2px solid transparent",
                        }}>{t}</div>
                      ))}
                    </div>
                    <div style={{ padding: "16px 16px" }}>
                      <div style={{
                        padding: "9px 12px", background: "#f6f5f2", borderRadius: 8,
                        fontSize: 12, color: "#aaa", marginBottom: 14,
                      }}>
                        Search documents...
                      </div>
                      <div style={{ textAlign: "center", padding: "14px 0", color: "#bbb", fontSize: 12, lineHeight: 1.5 }}>
                        {greeting}
                      </div>
                    </div>
                    <div style={{ padding: "4px 16px 10px", textAlign: "center", fontSize: 8, color: "#ccc" }}>
                      Powered by Clarifico AI
                    </div>
                  </div>
                  {/* FAB */}
                  <div style={{
                    width: 48, height: 48, borderRadius: "50%", background: widgetColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                    marginLeft: position === "bottom-right" ? "auto" : 0,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zm-9.18-6.95L7.4 14.46 10.94 18l5.66-5.66-1.41-1.41-4.24 4.24z" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div style={{
                  margin: 22, background: "#fff", borderRadius: 12,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.04)", overflow: "hidden",
                  border: `1px solid ${L.borderLight}`,
                }}>
                  <div style={{ padding: "13px 18px", background: widgetColor, color: "#fff" }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
                  </div>
                  <div style={{ display: "flex", borderBottom: "1px solid #f0eee9" }}>
                    {["Search", "Browse", "Ask AI"].map((t, i) => (
                      <div key={t} style={{
                        flex: 1, padding: "9px 4px", textAlign: "center", fontSize: 10, fontWeight: 600,
                        color: i === 0 ? widgetColor : "#aaa",
                        borderBottom: i === 0 ? `2px solid ${widgetColor}` : "2px solid transparent",
                      }}>{t}</div>
                    ))}
                  </div>
                  <div style={{ padding: "16px 16px", minHeight: 200 }}>
                    <div style={{
                      padding: "9px 12px", background: "#f6f5f2", borderRadius: 8,
                      fontSize: 12, color: "#aaa", marginBottom: 14,
                    }}>
                      Search documents...
                    </div>
                    {docCount > 0 ? (
                      <div>
                        {[1, 2, 3].map((i) => (
                          <div key={i} style={{
                            padding: "10px 0", borderBottom: `1px solid ${L.borderLight}`,
                            display: "flex", alignItems: "center", gap: 10,
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ height: 10, width: `${55 + i * 15}%`, background: "#e8e7e2", borderRadius: 3, marginBottom: 5 }} />
                              <div style={{ height: 7, width: `${35 + i * 10}%`, background: "#f0efe9", borderRadius: 2 }} />
                            </div>
                            <div style={{ padding: "3px 8px", background: "#f0efe9", borderRadius: 4, fontSize: 9, color: "#aaa" }}>PDF</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", padding: "28px 0", color: "#bbb", fontSize: 12 }}>{greeting}</div>
                    )}
                  </div>
                  <div style={{ padding: "4px 16px 10px", textAlign: "center", fontSize: 8, color: "#ccc" }}>
                    Powered by Clarifico AI
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* QR Code Generator */}
        <div style={{
          marginTop: 28, background: L.surface, border: `1px solid ${L.border}`,
          borderRadius: 12, padding: 24, animation: "ov-up 0.5s ease 0.15s both",
        }}>
          <QRGenerator />
        </div>
      </div>
    </Layout>
  );
}
